import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import { utcToPreferredLocalDateTime } from "../../../utils/timezoneDateConversion";

const resolveRawDateValue = (data, keys = []) => {
    for (const key of keys) {
        const value = data?.[key];
        if (value) return String(value).trim();
    }
    return "";
};

const formatDateTimeLocalWithoutShift = (value) => {
    if (!value) return "";

    const raw = String(value).trim();
    if (!raw) return "";

    const plainMatch = raw.match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
    );

    if (plainMatch) {
        const [, year, month, day, hour, minute] = plainMatch;
        return `${year}-${month}-${day}T${hour}:${minute}`;
    }

    return utcToPreferredLocalDateTime(raw, {
        separator: "T",
        withSeconds: false,
    }) || "";
};

const addOneMinuteToDateTimeLocal = (value) => {
    if (!value) return "";

    const match = value.match(
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
    );

    if (!match) return value;

    const [, year, month, day, hour, minute] = match;
    const date = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        0
    );

    if (Number.isNaN(date.getTime())) return value;

    date.setMinutes(date.getMinutes() + 1);

    const nextYear = date.getFullYear();
    const nextMonth = String(date.getMonth() + 1).padStart(2, "0");
    const nextDay = String(date.getDate()).padStart(2, "0");
    const nextHour = String(date.getHours()).padStart(2, "0");
    const nextMinute = String(date.getMinutes()).padStart(2, "0");

    return `${nextYear}-${nextMonth}-${nextDay}T${nextHour}:${nextMinute}`;
};

const buildUploadTimestamp = (value) => {
    if (!value) return "";

    const match = String(value).trim().match(
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
    );

    if (!match) return "";

    const [, year, month, day, hour, minute] = match;
    return `${year}_${month}_${day}_${hour}_${minute}_00`;
};

const sanitizeFloatInput = (value) => {
    return String(value ?? "").replace(/[^\d.-]/g, "");
};

const isValidFloatValue = (value) => {
    const trimmed = String(value ?? "").trim();

    if (!trimmed) return false;

    if (!/^[-+]?(?:\d+\.?\d*|\.\d+)$/.test(trimmed)) {
        return false;
    }

    return Number.isFinite(Number(trimmed));
};

const getDefaultStartDate = (data) =>
    addOneMinuteToDateTimeLocal(
        formatDateTimeLocalWithoutShift(
            resolveRawDateValue(data, [
                "InicioServicio",
                "fechainicio",
                "FechaHoraCita",
                "fecha",
            ])
        )
    );

const getDefaultEndDate = (data) =>
    formatDateTimeLocalWithoutShift(
        resolveRawDateValue(data, [
            "FinServicio",
            "fechafin",
            "FechaHoraDescargue",
        ])
    );

const ModalFoto = (props) => {
    const [latitud, setLatitud] = useState("");
    const [longitud, setLongitud] = useState("");
    const [fechaFoto, setFechaFoto] = useState(() => getDefaultStartDate(props.data));
    const [eventoSeleccionado, setEventoSeleccionado] = useState(
        props.eventoSeleccionado || props.listEventos[0]?.ID_Evento
    );
    const fileInputRef = useRef(null);
    const username = localStorage.getItem("username");

    const UploadPhoto = () => {
        const latitudValue = sanitizeFloatInput(latitud);
        const longitudValue = sanitizeFloatInput(longitud);

        if (!isValidFloatValue(latitudValue) || !isValidFloatValue(longitudValue)) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Latitud y Longitud deben ser floats válidos.",
                timer: 2000,
                showConfirmButton: false,
            });
            return;
        }

        try {
            const fechaLocal = buildUploadTimestamp(fechaFoto);
            if (!fechaLocal) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Fecha inválida. Selecciona una fecha y hora válidas.",
                    timer: 2000,
                    showConfirmButton: false,
                });
                return;
            }

            const fechacompleta = fechaLocal;
            const files = fileInputRef.current.files;
            const formData = new FormData();

            for (let i = 0; i < files.length; i += 1) {
                const newName = `${fechacompleta}_${i}_${props.device}_${props.placa}_${eventoSeleccionado}_${Math.floor(Math.random() * 1000)}_${username}_${latitudValue}_${longitudValue}_${props.selectedContrato}_${props.FKLokProyecto}_${props.FKICEmpresa}`;

                if (files[i].type.startsWith("image/")) {
                    formData.append("files", files[i], `${newName}.jpg`);
                } else if (files[i].type.startsWith("video/")) {
                    formData.append("files", files[i], `${newName}.mp4`);
                }
            }

            Swal.fire({
                title: "Subiendo Foto...",
                timerProgressBar: true,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            fetch(`${process.env.REACT_APP_SERVER}/upload`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("tkn")}`,
                },
                body: formData,
            })
                .then((response) => response.json())
                .then((data) => {
                    Swal.close();
                    if (Object.prototype.hasOwnProperty.call(data, "success")) {
                        Swal.fire({
                            icon: "success",
                            title: "Foto subida con éxito",
                            timer: 1000,
                            showConfirmButton: false,
                        });
                        props.onClose();
                    } else {
                        Swal.fire({
                            icon: "error",
                            title: "Error",
                            text: "No se pudo subir la foto.",
                        });
                    }
                })
                .catch((error) => {
                    Swal.close();
                    console.error("Error subiendo foto:", error);
                });
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Hubo un problema al procesar la solicitud.",
            });
        }
    };

    useEffect(() => {
        setFechaFoto(getDefaultStartDate(props.data));
    }, [props.data]);

    useEffect(() => {
        setEventoSeleccionado(props.eventoSeleccionado || props.listEventos[0]?.ID_Evento);
    }, [props.eventoSeleccionado, props.listEventos]);

    const minFecha = getDefaultStartDate(props.data) || undefined;
    const maxFecha = getDefaultEndDate(props.data) || undefined;

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="mb-3">
                        <label className="form-label" htmlFor="contrato">Contrato</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text">
                                <i className="bx bx-file-blank"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                value={props.selectedContrato}
                                readOnly
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label" htmlFor="latitud">Latitud</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-map-pin"></i></span>
                            <input
                                type="text"
                                className="form-control"
                                value={latitud}
                                inputMode="decimal"
                                placeholder="0.00"
                                onChange={(e) => setLatitud(sanitizeFloatInput(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label" htmlFor="longitud">Longitud</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-map-pin"></i></span>
                            <input
                                type="text"
                                className="form-control"
                                value={longitud}
                                inputMode="decimal"
                                placeholder="0.00"
                                onChange={(e) => setLongitud(sanitizeFloatInput(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label" htmlFor="fechafoto">Hora/Fecha</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-calendar"></i></span>
                            <input
                                id="fechafoto"
                                type="datetime-local"
                                className="form-control"
                                value={fechaFoto}
                                onChange={(e) => setFechaFoto(e.target.value)}
                                min={minFecha}
                                max={maxFecha}
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label" htmlFor="eventos">Evento</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-file-blank"></i></span>
                            <select
                                className="form-select"
                                value={eventoSeleccionado}
                                onChange={(e) => setEventoSeleccionado(e.target.value)}
                            >
                                {props.listEventos?.map((loc) => (
                                    <option key={loc.ID_Evento} value={loc.ID_Evento}>
                                        {loc.Descripcion}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label" htmlFor="fotos">Fotos</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-file"></i></span>
                            <input type="file" className="form-control" multiple ref={fileInputRef} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={props.onClose}>
                    Cerrar
                </button>
                <button type="button" className="btn btn-primary" onClick={UploadPhoto}>
                    Guardar
                </button>
            </div>
        </>
    );
};

export default ModalFoto;
